import {
  Flex,
  Input,
  InputGroup,
  InputRightAddon,
  useColorMode,
  useToast,
} from '@chakra-ui/react';
import {
  profitTextColor,
  profitBgColor,
  evaluateProfit,
  roundedPurchasePriceWithFees,
  setValueAsDecimalExtender,
  validateNotNaN,
} from '../shared';
import { useEffect } from 'react';
import { SalesContext } from '../Sales';
import { useTranslation } from 'react-i18next';
import { UpdateHistory } from '@texas/components/shared/autoUpdate/history/UpdateHistory';
import {
  AutoUpdateResponseCallback,
  Position,
} from '@texas/components/shared/autoUpdate/types';
import {
  useAutoUpdateForm,
  AutoUpdateWrapper,
} from '@texas/components/shared/autoUpdate/useAutoUpdateForm';
import {
  exchangeRateCalculation,
  getRoundedPurchasePriceWithFees,
  round,
} from '@texas/utils/helpers/numberHelpers';
import { MarginCalculation } from '../MarginCalculation';
import { articleHistoryApi } from '@texas/api/endpoints/articleHistoryApi';
import { PurchaseInformation } from '@texas/api/endpoints/purchaseInformationApi';
import { SalesCurrency } from '@texas/api/endpoints/salesCurrenciesApi';
import {
  TierPrice,
  AutoUpdateSalesPrice,
  SalesPrice,
  AutoUpdateSalesPriceAdditionalData,
  tierPriceApi,
  EMPTY_AUTO_UPDATE_SALES_PRICE_OBJECT,
} from '@texas/api/endpoints/tierPriceApi';
import { useActiveContext } from '@texas/hooks/useActiveContext';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';

interface SalesPriceForm {
  tierPrice: TierPrice;
  salesCurrency: SalesCurrency;
  purchaseInformation: PurchaseInformation;
  currencyBuyingRate: number;
  selectedCurrencyCode: string;
  showCurrencyCode?: boolean;
  isDisabled?: boolean;
  onPriceChanged?: (price: number) => void;
}

export function SalesPriceForm({
  showCurrencyCode = true,
  tierPrice,
  salesCurrency,
  purchaseInformation,
  currencyBuyingRate,
  selectedCurrencyCode,
  isDisabled = false,
  onPriceChanged = undefined,
}: SalesPriceForm) {
  const { colorMode } = useColorMode();
  const toast = useToast();
  const salesPrice = tierPrice.salesPrices.find(
    (s) => s.salesCurrencyId === salesCurrency.id,
  );
  const basePrice = tierPrice.basePrices.find(
    (b) => b.supplierId === purchaseInformation.supplierId,
  );

  const salesContext = useActiveContext(SalesContext);
  const { t } = useTranslation();

  const {
    registerAutoUpdate,
    watch,
    form,
    getValues,
    resetValue,
    setRequestParams,
    triggerSubmit,
    register,
    setValue,
    formState: { errors },
  } = useAutoUpdateForm<
    AutoUpdateSalesPrice,
    SalesPrice,
    AutoUpdateSalesPriceAdditionalData
  >({
    defaultValue: {
      price: salesPrice?.price,
    },
    requestParams: [salesPrice?.id ?? 0],
    namePrefix: 'tierPrice',
    autoUpdateRequest: tierPriceApi.autoUpdateSalesPrice,
    fallbackMapObject: EMPTY_AUTO_UPDATE_SALES_PRICE_OBJECT,
    additionalData: {
      tierPriceId: tierPrice.id,
      salesCurrencyId: salesCurrency.id,
    },
    onResponse,
  });

  useEffect(() => {
    const salesPriceValue = salesPrice?.price;
    if (salesPriceValue !== getValues('price')) {
      resetValue('price', salesPrice?.price);
    }
    if (salesPrice) {
      setRequestParams([salesPrice.id]);
    }
  }, [salesPrice, getValues, resetValue, setRequestParams]);

  function onResponse(
    data: AutoUpdateResponseCallback<SalesPrice, AutoUpdateSalesPrice>,
  ) {
    const toSet =
      salesContext.tierPrices.data?.map((t) => {
        if (t.id === tierPrice.id) {
          if (
            !t.salesPrices.find((s) => s.id === data.unmodifiedServerData.id)
          ) {
            t.salesPrices.push(data.unmodifiedServerData);
            return t;
          }

          t.salesPrices = t.salesPrices.map((s) => {
            if (s.id === data.unmodifiedServerData.id) {
              return { ...s, ...data.mappedServerData };
            }

            return s;
          });
        }

        return t;
      }) ?? [];

    setRequestParams([data.unmodifiedServerData.id]);

    salesContext.tierPrices.set(toSet);
  }

  const watchPrice = watch('price');

  useEffect(() => {
    if (!onPriceChanged) return;
    onPriceChanged(watchPrice ?? 0);
  }, [onPriceChanged, watchPrice]);

  const priceProfit = evaluateProfit(
    exchangeRateCalculation(
      watchPrice ?? 0,
      salesCurrency.fixedExchangeRate,
      currencyBuyingRate,
      tierPrice.decimalsInPrice,
    ),
    roundedPurchasePriceWithFees(
      basePrice?.price ?? 0,
      currencyBuyingRate,
      tierPrice.decimalsInPrice,
      purchaseInformation,
    ),
  );

  const color = profitTextColor(priceProfit, colorMode === 'dark');
  const bgColor = profitBgColor(priceProfit, colorMode === 'dark');

  return (
    <form style={{ width: 'fit-content' }} {...form}>
      <fieldset>
        <AutoUpdateWrapper
          autoUpdateProps={registerAutoUpdate}
          path="price"
          position={Position.Right}
        >
          <InputGroup
            alignItems="center"
            padding={1}
            pr={0}
            bg={bgColor}
            w={56}
            borderRadius={4}
          >
            <Input
              {...register('price', {
                setValueAs: (value) => setValueAsDecimalExtender(value, true),
                validate: (value) => validateNotNaN(value, t),
                onBlur: () => {
                  const value = getValues('price');
                  if (value != null) {
                    const rounded = round(value, tierPrice.decimalsInPrice);
                    if (rounded !== value) {
                      toast({
                        title: t('general.valueRounded'),
                        status: 'info',
                        duration: 3000,
                        isClosable: true,
                      });
                    }
                    setValue('price', rounded);
                  }
                  triggerSubmit();
                },
              })}
              color={color}
              role="group"
              _placeholder={{
                color: color,
              }}
              placeholder="0"
              disabled={isDisabled}
            />
            {showCurrencyCode && (
              <Flex gap={1}>
                <InputRightAddon>{salesCurrency.currencyCode}</InputRightAddon>
                <InputRightAddon>
                  <UpdateHistory
                    header={t('history.salesPriceHistory')}
                    historyRequestWrapper={() =>
                      articleHistoryApi.salesPrice(
                        salesContext.articleId,
                        tierPrice.id,
                        salesCurrency.id,
                      )
                    }
                  />
                  <MarginCalculation
                    purchasePrice={getRoundedPurchasePriceWithFees(
                      basePrice?.price ?? 0,
                      purchaseInformation.freightCostValue,
                      purchaseInformation.handlingFeeValue,
                      tierPrice.decimalsInPrice,
                    )}
                    purchaseExchangeRate={purchaseInformation.fixedExchangeRate}
                    salesPrice={watchPrice ?? 0}
                    salesExchangeRate={salesCurrency.fixedExchangeRate}
                    targetExchangeRate={currencyBuyingRate}
                    targetCurrencyCode={selectedCurrencyCode}
                    profit={priceProfit}
                    decimalsInPrice={tierPrice.decimalsInPrice}
                  />
                </InputRightAddon>
              </Flex>
            )}
          </InputGroup>
          <ErrorLabel text={errors.price?.message} />
        </AutoUpdateWrapper>
      </fieldset>
    </form>
  );
}
